/**
 * /reports/tax-depreciation — annual MACRS tax depreciation report.
 *
 * Per-asset §179 / bonus / MACRS / total for the selected tax year, plus
 * entity totals and which convention (half-year vs mid-quarter) the year
 * resolved to. See MONEY-TAX-DEPRECIATION-001-spec.md §5.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, queryOptions } from "@tanstack/react-query";
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/reports/tax-depreciation")({
  component: TaxDepreciationReportPage,
});

interface TaxDepreciationRow {
  asset_id: string;
  asset_no: string;
  description: string;
  macrs_class: string | null;
  convention: string | null;
  sec179: number;
  bonus: number;
  macrs: number;
  total: number;
}

interface TaxDepreciationReport {
  year: number;
  personal_property_convention: string | null;
  rows: TaxDepreciationRow[];
  totals: { sec179: number; bonus: number; macrs: number; total: number };
}

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 1990;
const MAX_YEAR = 2099;

const taxDepreciationReportQueryOptions = (year: number) =>
  queryOptions({
    queryKey: ["reports", "tax-depreciation", year],
    queryFn: () => api<TaxDepreciationReport>(`/api/reports/tax-depreciation?year=${year}`),
  });

function fmtMoney(v: string | number | null | undefined) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function TaxDepreciationReportPage() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const reportQ = useQuery(taxDepreciationReportQueryOptions(year));
  const rows = reportQ.data?.rows ?? [];
  const totals = reportQ.data?.totals ?? {
    sec179: rows.reduce((s, r) => s + Number(r.sec179 ?? 0), 0),
    bonus: rows.reduce((s, r) => s + Number(r.bonus ?? 0), 0),
    macrs: rows.reduce((s, r) => s + Number(r.macrs ?? 0), 0),
    total: rows.reduce((s, r) => s + Number(r.total ?? 0), 0),
  };

  function exportCsv() {
    const header = ["asset_no", "description", "macrs_class", "convention", "sec179", "bonus", "macrs", "total"];
    const lines = [header.join(",")];
    for (const r of rows) {
      const cells = [
        r.asset_no, r.description, r.macrs_class ?? "", r.convention ?? "",
        r.sec179, r.bonus, r.macrs, r.total,
      ].map((c) => {
        const s = String(c);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      });
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tax-depreciation-${year}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ReceiptLongIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Tax Depreciation</Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <TextField
          label="Tax Year" type="number" size="small" sx={{ minWidth: 140 }}
          inputProps={{ min: MIN_YEAR, max: MAX_YEAR }}
          value={year}
          onChange={(e) => {
            // Skip the transient empty string while the user is retyping —
            // only commit once it parses, so backspacing doesn't snap back.
            if (e.target.value === "") return;
            const n = Number(e.target.value);
            if (Number.isFinite(n)) setYear(n);
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<DownloadIcon />} onClick={exportCsv} disabled={rows.length === 0}>
          Export CSV
        </Button>
      </Stack>

      {reportQ.data?.personal_property_convention && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {year} resolved to the <strong>{reportQ.data.personal_property_convention.replace(/_/g, " ")}</strong> convention.
        </Alert>
      )}

      {reportQ.isLoading && <CircularProgress size={20} />}
      {reportQ.error && <Alert severity="error">Failed to load report.</Alert>}
      {!reportQ.isLoading && !reportQ.error && rows.length === 0 && (
        <Alert severity="info">No tax depreciation for {year}. Assets need a tax profile set (Assets → Schedule → Tax tab).</Alert>
      )}

      {rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell>Asset #</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Convention</TableCell>
                <TableCell align="right">§179</TableCell>
                <TableCell align="right">Bonus</TableCell>
                <TableCell align="right">MACRS</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.asset_id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{r.asset_no}</TableCell>
                  <TableCell>{r.description}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{r.macrs_class ? `${r.macrs_class}-yr` : "—"}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{r.convention?.replace(/_/g, " ") ?? "—"}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.sec179)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.bonus)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.macrs)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600 }}>{fmtMoney(r.total)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ "& td": { fontWeight: 700, borderTop: 2, borderColor: "divider" } }}>
                <TableCell colSpan={4}>Total</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.sec179)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.bonus)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.macrs)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
